import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import type { FilterState } from 'framework/ViewTypes/components';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import type { SourcingFilterKey, SourcingFilterOptions, SourcingFilters, SourcingSavedFilter } from '../../types';
import useRecruitmentPoolSourcingService from '../../useRecruitmentPoolSourcingService';
import { SOURCING_FILTERS } from './constants';
import {
  isSourcingSelectionComplete,
  mapCollectionFiltersToSourcingFilters,
  mapSourcingFiltersToSavedParameters,
  mapSourcingParametersToFilters,
  resolveInitialSourcingFilters,
  shouldOpenInitialSourcingSettings,
  toggleSourcingFilterMode,
} from './utils/sourcingFilterModel';
import { cloneFilters, findMatchingSavedSourcingFilter } from './utils/utils';

type UseRecruitmentPoolSourcingFiltersParams = {
  recruitmentPoolId: string;
  filterOptions: SourcingFilterOptions | undefined;
  filters: FilterState;
  onApply: (filters: SourcingFilters) => void;
};

const removeFilter = (filters: SourcingFilters, key: SourcingFilterKey): SourcingFilters => {
  if (key === SOURCING_FILTERS.specialization || key === SOURCING_FILTERS.technology) return filters;
  const next = cloneFilters(filters);
  delete next[key];
  next.stricts = (next.stricts ?? []).filter((strictKey: string) => strictKey !== key);
  return next;
};

export const useRecruitmentPoolSourcingFilters = ({
  recruitmentPoolId,
  filterOptions,
  filters,
  onApply,
}: UseRecruitmentPoolSourcingFiltersParams) => {
  const { addMessage } = useMessageService();
  const { getSavedFilters, saveFilter } = useRecruitmentPoolSourcingService();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SourcingSavedFilter[]>([]);
  const [savedFiltersLoaded, setSavedFiltersLoaded] = useState(false);

  const savedFiltersRequestId = useRef(0);
  const openedForOptionsId = useRef<string>();
  const addMessageRef = useRef(addMessage);

  const sourcingFilters = useMemo(() => mapCollectionFiltersToSourcingFilters(filters), [filters]);

  const reloadSavedFilters = useCallback(async () => {
    const requestId = ++savedFiltersRequestId.current;

    try {
      const nextFilters = await getSavedFilters(recruitmentPoolId);
      if (requestId === savedFiltersRequestId.current) {
        setSavedFilters(nextFilters);
        setSavedFiltersLoaded(true);
      }
      return nextFilters;
    } catch (error) {
      if (requestId === savedFiltersRequestId.current) setSavedFiltersLoaded(true);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruitmentPoolId]);

  useEffect(() => {
    let isMounted = true;
    void reloadSavedFilters().catch(() => {
      if (isMounted) addMessageRef.current(new ErrorMessage('Unable to load saved sourcing filters.'));
    });

    return () => {
      isMounted = false;
      savedFiltersRequestId.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSavedFilters]);

  useEffect(() => {
    if (!filterOptions) return;
    const nextFilters = resolveInitialSourcingFilters(filterOptions, sourcingFilters);

    if (!isEqual(sourcingFilters, nextFilters)) {
      onApply(nextFilters);
    }

    if (openedForOptionsId.current !== filterOptions.id) {
      openedForOptionsId.current = filterOptions.id;
      setSettingsOpen(shouldOpenInitialSourcingSettings(filterOptions, nextFilters));
    }
  }, [filterOptions, onApply, sourcingFilters]);

  const matchingSavedFilter = useMemo(
    () => findMatchingSavedSourcingFilter(sourcingFilters, savedFilters),
    [savedFilters, sourcingFilters],
  );
  const canSaveFilter = filterOptions ? isSourcingSelectionComplete(filterOptions, sourcingFilters) : false;
  const appliedSavedFilterName = matchingSavedFilter?.name;

  const apply = useCallback(
    (candidateFilters: SourcingFilters, openIncompleteSettings = false) => {
      if (!filterOptions) return;
      const nextFilters = resolveInitialSourcingFilters(filterOptions, candidateFilters);
      onApply(nextFilters);
      if (openIncompleteSettings && !isSourcingSelectionComplete(filterOptions, nextFilters)) {
        setSettingsOpen(true);
      }
    },
    [filterOptions, onApply],
  );

  const handleChipModeToggle = useCallback(
    (key: SourcingFilterKey) => {
      if (key === SOURCING_FILTERS.specialization || key === SOURCING_FILTERS.technology) return;
      apply(toggleSourcingFilterMode(sourcingFilters, key));
    },
    [apply, sourcingFilters],
  );

  const handleSave = useCallback(
    async (name: string, selectedFilters: SourcingFilters) => {
      const parameters = mapSourcingFiltersToSavedParameters(selectedFilters);
      if (!parameters) throw new Error('Specialization is required.');
      const saved = await saveFilter(recruitmentPoolId, name, parameters);
      setSavedFilters(current => [saved, ...current.filter(filter => filter.id !== saved.id)]);
      apply(mapSourcingParametersToFilters(saved.parameters));
    },
    [apply, recruitmentPoolId, saveFilter],
  );

  return {
    apply,
    appliedSavedFilterName,
    canSaveFilter,
    handleChipModeToggle,
    matchingSavedFilter,
    onDeleteSavedFilters: (ids: string[]) => {
      setSavedFilters(current => current.filter(filter => !ids.includes(filter.id)));
    },
    onOpenSaved: () => setSavedOpen(true),
    onOpenSave: () => setSaveOpen(true),
    onOpenSettings: () => setSettingsOpen(true),
    onRemoveFilter: (key: SourcingFilterKey) => apply(removeFilter(sourcingFilters, key)),
    reloadSavedFilters,
    saveOpen,
    savedFilters,
    savedFiltersLoaded,
    savedOpen,
    saveSourcingFilter: handleSave,
    setSaveOpen,
    setSavedOpen,
    setSettingsOpen,
    settingsOpen,
    sourcingFilters,
  };
};
